import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer from "@insite/mobius/Drawer";
import Hidden from "@insite/mobius/Hidden";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Cart from "@sonic/Assets/Icons/Cart";
import ChevronsDown from "@sonic/Assets/Icons/ChevronsDown";
import ChevronsUp from "@sonic/Assets/Icons/ChevronsUp";
import ProductAddToCartButton from "@sonic/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink from "@sonic/Overrides/Components/ProductAddToListLink";
import ProductPrice from "@sonic/Overrides/Components/ProductPrice";
import React, { FC } from "react";
import { css } from "styled-components";

export interface ProductDetailDrawerStyles {
    topToggleContainer?: InjectableCss;
    bottomDrawerContainer?: InjectableCss;
    buttonContainer?: InjectableCss;
    toggleButton?: ButtonPresentationProps;
    drawerContainer?: InjectableCss;
    primaryButton?: ButtonPresentationProps;
}

interface OwnProps {
    product: ProductContextModel;
}

type Props = OwnProps;

export const productDetailDrawerStyles = {
    topToggleContainer: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            margin-top: -28px;
        `,
    },
    toggleButton: {
        borderColer: "none",
        typographyProps: {
            size: 11,
            color: "text.main",
        },
        css: css`
            display: flex;
            border: none;
            padding: 0;
            justify-content: center;
            align-items: center;
        `,
    },
    bottomDrawerContainer: {
        css: css`
            width: 100%;
            display: flex;
            flex-wrap: wrap;
            padding: 28px 16px;
            background-color: ${getColor("common.background")};
            justify-content: space-between;
        `,
    },
    buttonContainer: {
        css: css`
            flex-direction: row;
            flex-wrap: wrap;
            display: flex;
            align-items: flex-end;
            justify-content: flex-end;
            gap: 20px;
            flex: 1;
        `,
    },
    primaryButton: {
        typographyProps: {
            size: 16,
            fontFamily: "Pancetta W05 SemiBold",
        },
    },
};

export const ProductDetailsPageDrawer: FC<Props> = ({ children, product }) => {
    const [openDrawer, setOpenDrawer] = React.useState(false);

    const IconSource = openDrawer ? ChevronsDown : ChevronsUp;

    return (
        <Hidden above="sm">
            <StyledWrapper {...productDetailDrawerStyles.bottomDrawerContainer}>
                <StyledWrapper {...productDetailDrawerStyles.topToggleContainer}>
                    <Button
                        {...productDetailDrawerStyles.toggleButton}
                        color="text.main"
                        variant="primary"
                        onClick={() => setOpenDrawer(!openDrawer)}
                        icon={{ src: IconSource, position: "left" }}
                        sizeVariant="small"
                    >
                        {openDrawer && translate("Close")}
                    </Button>
                </StyledWrapper>
                <ProductPrice product={product} />
                <StyledWrapper {...productDetailDrawerStyles.buttonContainer}>
                    {openDrawer && <ProductAddToListLink />}
                    {!openDrawer && (
                        <Button
                            {...productDetailDrawerStyles.primaryButton}
                            onClick={() => setOpenDrawer(!openDrawer)}
                            variant="tertiary"
                        >
                            {translate("More Details")}
                        </Button>
                    )}
                    {openDrawer && <ProductAddToCartButton icon={{ src: Cart, position: "right" }} />}
                </StyledWrapper>
            </StyledWrapper>
            <Drawer
                cssOverrides={{
                    drawerTitle: css`
                        display: none;
                    `,
                    drawerContent: css`
                        padding: 15px;
                    `,
                }}
                position="bottom"
                isOpen={openDrawer}
                handleClose={() => setOpenDrawer(!openDrawer)}
                size={640}
            >
                {children}
            </Drawer>
        </Hidden>
    );
};
